import Path2D from "./../geometries/path2d.ts";
import { Color } from "./../math/color.ts";
import { Matrix3d } from "../math/matrix3d.ts";
import { Vector2d } from "../math/vector2d.ts";
import { CANVAS_ONRESIZE, emit } from "../system/event.ts";
import { createCanvas } from "./canvas_factory.js";
import { Gradient } from "./gradient.js";
import RenderState from "./renderstate.js";
import CanvasRenderTarget from "./rendertarget/canvasrendertarget.js";

/**
 * @import RenderTargetPool from "./rendertarget/render_target_pool.js";
 */

/**
 * @import {Rect} from "./../geometries/rectangle.ts";
 * @import {RoundRect} from "./../geometries/roundrect.ts";
 * @import {Polygon} from "../geometries/polygon.ts";
 * @import {Line} from "./../geometries/line.ts";
 * @import {Ellipse} from "./../geometries/ellipse.ts";
 * @import {Bounds} from "./../physics/bounds.ts";
 * @import {default as Texture2d} from "./texture/texture2d.ts";
 */

/**
 * The drain site's own view, held while the transparent pass installs each
 * entry's. One for the whole engine — the pass is never re-entered, which
 * `_transparentFlushing` enforces.
 * @ignore
 * @internal
 */
const _savedView = new Matrix3d();

/**
 * a base renderer object
 * @category Rendering
 */
export default class Renderer {
	/**
	 * @param {ApplicationSettings} [options] - optional parameters for the renderer
	 */
	constructor(options) {
		/**
		 * The renderer renderTarget
		 * @type {CanvasRenderTarget}
		 */
		this.renderTarget = new CanvasRenderTarget(
			options.width,
			options.height,
			// support case when a global canvas is available, e.g. webapp adapter for wechat
			typeof globalThis.canvas !== "undefined"
				? Object.assign(options, { canvas: globalThis.canvas })
				: options,
		);

		/**
		 * The given constructor options
		 * @public
		 * @type {object}
		 */
		this.settings = options;

		/**
		 * the requested video size ratio
		 * @public
		 * @type {number}
		 */
		this.designRatio = this.settings.width / this.settings.height;

		/**
		 * the scaling ratio to be applied to the main canvas
		 * @type {Vector2d}
		 * @default <1,1>
		 */
		this.scaleRatio = new Vector2d(this.settings.scale, this.settings.scale);

		/**
		 * true if the current rendering context is valid
		 * @default true
		 * @type {boolean}
		 */
		this.isContextValid = true;

		/**
		 * The GPU device identifier string (set by GPU renderers — WebGL today,
		 * WebGPU once it lands; undefined for Canvas).
		 * @type {string|undefined}
		 */
		this.GPURenderer = undefined;

		/**
		 * an optional custom shader to use instead of the default one.
		 * Set by a renderable's preDraw when a shader is assigned.
		 * (GPU renderers only; ignored by Canvas renderer)
		 * @type {GLShader|ShaderEffect|undefined}
		 * @ignore
		 * @internal
		 */
		this.customShader = undefined;

		/**
		 * The render target pool for post-effect processing (ping-pong FBOs).
		 * Initialized by GPU renderers (WebGL, WebGPU). Null on Canvas renderer.
		 * @type {RenderTargetPool|null}
		 * @ignore
		 * @internal
		 */
		this._renderTargetPool = null;

		/**
		 * The Path2D instance used by the renderer to draw primitives
		 * @type {Path2D}
		 */
		this.path2D = new Path2D();

		/**
		 * The renderer backend identity — the built-in renderers report
		 * `"CANVAS"`, `"WebGL2"` and `"WebGPU"`. Use it for identity
		 * checks (code coupled to one backend's machinery); prefer the
		 * capability flags (`shaderLanguage`, `supportsDepthBuffer`,
		 * `supportsRetainedMesh`, `supportsShaderTileLayers`) when the
		 * requirement is a capability rather than a specific backend.
		 * (override this property with a specific value when implementing
		 * a custom renderer)
		 * @type {string}
		 */
		this.type = "Generic";

		/**
		 * The {@link Application} this renderer belongs to, set by
		 * `Application.init()` — engine code holding a renderer reference
		 * must use this rather than the global game instance.
		 * @type {Application|undefined}
		 */
		this.parentApplication = undefined;

		/**
		 * Whether this renderer backend can draw TMX tile layers through a
		 * GPU shader path (see the `gpuTilemap` application setting).
		 * `false` here on the base/Canvas renderer; GPU backends flip it.
		 * A capability flag rather than a version/class check so future
		 * backends advertise support without consumers growing type checks.
		 * @type {boolean}
		 * @default false
		 */
		this.supportsShaderTileLayers = false;

		/**
		 * Whether this renderer backend can keep mesh geometry resident on the
		 * GPU, letting a mesh supply a model matrix instead of vertices it has
		 * already positioned itself. `false` here on the base/Canvas renderer.
		 * @type {boolean}
		 * @default false
		 */
		this.supportsRetainedMesh = false;

		/**
		 * Whether this renderer backend can draw one geometry many times in a
		 * single call from per-instance data — what {@link InstancedMesh}
		 * needs. `false` here on the base/Canvas renderer, which falls back to
		 * drawing each instance individually.
		 * @type {boolean}
		 * @default false
		 */
		this.supportsInstancing = false;

		/**
		 * The source language this backend accepts for user-supplied shaders,
		 * or `null` when it has no programmable pipeline at all (the Canvas
		 * backend). `"glsl"` on the WebGL backend, `"wgsl"` on the WebGPU
		 * backend.
		 *
		 * Consumers that need a *specific* language — `ShaderEffect` and the
		 * loader's `{vertex, fragment}` shader assets both hand GLSL source
		 * straight to the driver — must compare against the language rather
		 * than test for a GPU backend, or they would accept a backend that
		 * cannot read what they are about to give it.
		 * @type {"glsl"|"wgsl"|null}
		 * @default null
		 */
		this.shaderLanguage = null;

		/**
		 * Whether this backend has a depth buffer, and with it the 3D
		 * projection and mesh paths a depth-sorted scene needs (`Camera3d`
		 * and any camera declaring `defaultSortOn = "depth"`). `false` here
		 * on the base/Canvas renderer.
		 * @type {boolean}
		 * @default false
		 */
		this.supportsDepthBuffer = false;

		/**
		 * The background color used to clear the main framebuffer.
		 * Note: alpha value will be set based on the transparent property of the renderer settings.
		 * @default black
		 * @type {Color}
		 */
		this.backgroundColor = new Color(
			0,
			0,
			0,
			this.settings.transparent ? 0.0 : 1.0,
		);

		/**
		 * The renderer state container (color, tint, transform, scissor, blend mode)
		 * with a zero-allocation save/restore stack.
		 * @type {RenderState}
		 */
		this.renderState = new RenderState();

		// expose renderState properties directly (same references)
		this.currentColor = this.renderState.currentColor;
		this.currentTint = this.renderState.currentTint;
		this.currentScissor = this.renderState.currentScissor;

		/**
		 * The thickness of lines for shape drawing operations like
		 * {@link Renderer#strokeRect}, {@link Renderer#strokeEllipse}
		 * and {@link Renderer#strokeLine}. Subclasses override the
		 * storage — `CanvasRenderer` proxies it to the underlying 2D
		 * context via a getter/setter, while `WebGLRenderer` treats it
		 * as a regular field consumed by its shape-stroke routines.
		 * @type {number}
		 * @default 1
		 */
		this.lineWidth = 1;

		/**
		 * @ignore
		 * @internal
		 */
		this.maskLevel = 0;

		// the projectionMatrix (set through setProjection)
		this.projectionMatrix = new Matrix3d();

		/**
		 * The distance fog installed by the camera currently drawing, or
		 * `null` for none. Written once per camera by `Camera2d.draw`; read
		 * per draw by the mesh batchers. Null for every camera that is not a
		 * `Camera3d` with fog enabled, which is the default.
		 * @ignore
		 * @internal
		 */
		this._fog3d = null;

		// default uvOffset
		this.uvOffset = 0;

		/**
		 * The normal-map texture associated with the next `drawImage` call,
		 * if any. Set by `Sprite.draw` (and any other normal-map-aware
		 * renderable) just before calling `drawImage`, then cleared back
		 * to `null` after. The WebGL renderer reads this state and routes
		 * lit quads through the shader's lighting path; the Canvas
		 * renderer ignores it entirely.
		 * @type {HTMLImageElement|HTMLCanvasElement|OffscreenCanvas|ImageBitmap|null}
		 */
		this.currentNormalMap = null;

		/**
		 * Number of active `Light2d` instances uploaded to the lit batcher
		 * for the current frame. Set by `setLightUniforms`. The WebGL
		 * renderer's `drawImage` reads this to decide between the unlit
		 * fast-path batcher (default) and the lit batcher (only when
		 * lights AND a normal map are both in play).
		 * @type {number}
		 */
		this.activeLightCount = 0;
	}

	/**
	 * @type {string}
	 */
	get currentBlendMode() {
		return this.renderState.currentBlendMode;
	}

	set currentBlendMode(value) {
		this.renderState.currentBlendMode = value;
	}

	/**
	 * Current per-renderable depth value. The GPU batchers (WebGL and
	 * WebGPU) push it into the vertex stream as the `z`
	 * component of each vertex — a no-op under the default orthographic
	 * projection, used by perspective (Camera3d) to
	 * scale and parallax sprites by distance. Mirrors `renderable.depth`,
	 * set automatically by `Renderable.preDraw` via {@link Renderer#setDepth}.
	 * @type {number}
	 * @default 0
	 */
	get currentDepth() {
		return this.renderState.currentDepth;
	}

	set currentDepth(value) {
		this.renderState.currentDepth = value;
	}

	/**
	 * return the height of the canvas which this renderer draws to
	 * @returns {number} height of the system Canvas
	 */
	get height() {
		return this.getCanvas().height;
	}

	set height(value) {
		this.resize(this.width, value);
	}

	/**
	 * return the width of the canvas which this renderer draws to
	 * @returns {number} width of the system Canvas
	 */
	get width() {
		return this.getCanvas().width;
	}

	set width(value) {
		this.resize(value, this.height);
	}

	/**
	 * Acquire whatever the backend cannot acquire synchronously — awaited by
	 * {@link Application#init} right after the renderer is constructed, and
	 * the reason `Application#init` is asynchronous at all. The Canvas and
	 * WebGL backends have their context by the end of the constructor and
	 * resolve immediately (this default); the WebGPU backend performs its
	 * adapter/device negotiation here.
	 * @returns {Promise<void>} resolves once the renderer is ready to draw
	 */
	init() {
		return Promise.resolve();
	}

	/**
	 * prepare the framebuffer for drawing a new frame
	 */
	clear() {}

	/**
	 * render the main framebuffer on screen
	 */
	flush() {}

	/**
	 * Mark the start of a screen-space (`floating`) draw, during which the
	 * camera's screen projection is installed and world-space geometry cannot
	 * be replayed. Balanced by {@link Renderer#endScreenSpace}.
	 * @ignore
	 * @internal
	 */
	beginScreenSpace() {
		/** @ignore @internal */
		this._screenSpaceDepth = (this._screenSpaceDepth ?? 0) + 1;
	}

	/**
	 * Put per-frame renderer state back to a known-good baseline.
	 *
	 *
	 * `Container.draw` opens the bracket around a floating child without a
	 * `finally`, so a child that throws never closes it — and while it stays
	 * open every drain of the transparent queue is skipped, which would
	 * silently disable the pass for the rest of the session and grow the queue
	 * a frame at a time. Called once per frame, where the count is always zero
	 * in normal operation.
	 * @ignore
	 * @internal
	 */
	resetFrameState() {
		this._screenSpaceDepth = 0;
		// Purge anything a previous frame left queued. Only `reset()` did this,
		// and that runs on a stage change, not per frame — so a frame abandoned
		// between `beginPostEffect` and `endPostEffect` left entries keyed to a
		// render target whose key the pool then hands to a LATER pass, which
		// would replay a dead frame's geometry into its own buffer. Empty in
		// normal operation: every queue is drained by the frame that filled it.
		for (const queue of this._transparentQueues ?? []) {
			if (queue === undefined || queue.count === 0) {
				continue;
			}
			for (let i = 0; i < queue.count; i++) {
				queue.pool[i].mesh = null;
				queue.pool[i].instanced = null;
				queue.pool[i].shader = null;
			}
			queue.count = 0;
		}
	}

	/**
	 * Mark the end of a screen-space draw.
	 * @ignore
	 * @internal
	 */
	endScreenSpace() {
		const depth = (this._screenSpaceDepth ?? 0) - 1;
		this._screenSpaceDepth = depth > 0 ? depth : 0;
	}

	/**
	 * Hold one draw back for the transparent pass.
	 *
	 * The mesh tier renders in two phases. The **opaque pass** writes depth and
	 * runs in whatever order the container sorted; the **transparent pass**
	 * replays this queue afterwards, back-to-front, with blending on and depth
	 * writes off. Depth *testing* stays on throughout, so a transparent object
	 * is still correctly hidden behind opaque geometry in front of it.
	 *
	 * Both halves are necessary. Blending needs back-to-front order to
	 * composite correctly, and a draw that writes no depth has nothing to
	 * defend itself with — any opaque mesh drawn afterwards simply paints over
	 * it. A ground plane does exactly that whenever it sorts after the props
	 * standing on it, which is the common case, because a large plane's single
	 * sort key says nothing about where it sits relative to what stands on it.
	 * @param {object} mesh - the renderable to replay
	 * @param {Matrix3d} modelMatrix - where it sits (COPIED, not retained: the
	 * decal clients share one quad whose matrix the next caster overwrites long
	 * before this entry is drawn)
	 * @param {number} tint - packed ARGB tint for the draw
	 * @param {string} [blend] - blend mode for this entry alone
	 * @param {object} [instanced] - the `InstancedMesh` whose instance buffer
	 * supplies one draw per instance, for the instanced tier
	 * @ignore
	 * @internal
	 */
	queueTransparent(mesh, modelMatrix, tint, blend, instanced) {
		const queue = this.transparentQueue();
		const pool = queue.pool;
		const at = queue.count;
		// Slots are reused frame to frame, so a steady scene allocates nothing
		// after its first.
		let entry = pool[at];
		if (entry === undefined) {
			entry = {
				mesh: null,
				matrix: new Matrix3d(),
				view: new Matrix3d(),
				tint: 0,
				blend: "normal",
				key: 0,
				instanced: null,
				shader: null,
			};
			pool[at] = entry;
		}
		entry.mesh = mesh;
		entry.matrix.copy(modelMatrix);
		// The view the entry was queued UNDER, captured because the replay
		// happens somewhere else entirely. Both backends read the view live off
		// `currentTransform` at draw time, and `Container.draw` translates by
		// its own position before walking its children — so a mesh queued
		// inside a positioned container and replayed after that bracket closed
		// would draw at the container's offset from where it belongs. Visible
		// as a jump the instant a mesh starts fading.
		entry.view.copy(this.currentTransform);
		entry.tint = tint;
		entry.blend = blend ?? "normal";
		entry.instanced = instanced ?? null;
		// The custom shader hosted on this draw, for the same reason the blend
		// mode is held here: both backends read it LIVE off the renderer, and
		// by the time the replay runs the renderable's `postDraw` has restored
		// it to whatever was current before. A mesh with a custom shader was
		// silently losing it the moment it faded — drawn with the built-in
		// shading instead, at full opacity too under `transparent: true`.
		entry.shader = this.customShader ?? null;
		// Squared distance from the camera, taken once here rather than per
		// comparison. Radial rather than view-space z for the same reason the
		// fog distance is: no sign-convention trap, and stable as the camera
		// turns.
		//
		// Measured by pushing the position THROUGH the view, not by extracting
		// the eye from it. The eye is recoverable as -Rᵀ·t only when the upper
		// 3×3 is orthonormal, and the accumulated transform here is not just
		// the camera's: `Container.draw` folds every ancestor into it, so one
		// scaled container makes that extraction wrong and silently mis-orders
		// the pass. The length of the view-space position needs no such
		// assumption, and agrees exactly with the old form whenever the view
		// really is rigid — a rotation preserves length.
		// Measured at the GEOMETRY's centre, not at the model matrix's
		// translation. The translation is the mesh's ORIGIN, and a mesh whose
		// vertices run away from its origin sits nowhere near it — a ground
		// plane authored from one corner, or a river plane parented to the
		// boat it trails behind. Keyed on the origin, two meshes occupying the
		// identical world volume sort differently depending on where their
		// author put it, and a long plane whose origin sits at the near end
		// keys as the nearest thing in the scene and is replayed over
		// everything inside its own span.
		//
		// `_updateLocalCenter` caches per geometry edit, and resolves to the
		// origin for an origin-centred mesh, which is most of them.
		const v = this.currentTransform.val;
		const m = modelMatrix.val;
		let cx = 0;
		let cy = 0;
		let cz = 0;
		if (typeof mesh?._updateLocalCenter === "function") {
			mesh._updateLocalCenter();
			cx = mesh._lcx;
			cy = mesh._lcy;
			cz = mesh._lcz;
		}
		// that centre through the model matrix, giving its world position
		const mx = m[0] * cx + m[4] * cy + m[8] * cz + m[12];
		const my = m[1] * cx + m[5] * cy + m[9] * cz + m[13];
		const mz = m[2] * cx + m[6] * cy + m[10] * cz + m[14];
		const dx = v[0] * mx + v[4] * my + v[8] * mz + v[12];
		const dy = v[1] * mx + v[5] * my + v[9] * mz + v[13];
		const dz = v[2] * mx + v[6] * my + v[10] * mz + v[14];
		entry.key = dx * dx + dy * dy + dz * dz;
		queue.count = at + 1;
	}

	/**
	 * The render target a queued entry belongs to, as a stable small integer
	 * (`-1` for the screen).
	 *
	 * Both GPU backends run their post-effect passes through the shared
	 * {@link RenderTargetPool}, whose `activeBase` names the innermost active
	 * pass — so this is one number that means the same thing on both, and on
	 * the Canvas renderer (which has no pool, and never queues) it is
	 * constant.
	 * @returns {number} the current target's key
	 * @ignore
	 * @internal
	 */
	transparentTarget() {
		return this._renderTargetPool?.activeBase ?? -1;
	}

	/**
	 * The transparent queue belonging to one render target, created on first
	 * use.
	 *
	 * There is one queue **per target**, not one per renderer, and that is the
	 * whole point: a queue can only ever be replayed into the target its
	 * entries were recorded for. A post effect binds its own target part-way
	 * through a scene, and a drain that fires inside that bracket used to
	 * replay the *world's* queued geometry into the effect's offscreen buffer
	 * — baking the scene's transparent objects into one renderable's texture.
	 * Keyed this way the two never meet: the effect's drain sees only what was
	 * queued inside the effect, and the world's queue waits for the camera.
	 * @param {number} [key] - the target, defaulting to the current one
	 * @returns {{pool: object[], count: number}} that target's queue
	 * @ignore
	 * @internal
	 */
	transparentQueue(key = this.transparentTarget()) {
		/** @ignore @internal */
		const queues = (this._transparentQueues ??= []);
		// `-1` is the screen, so shift into a dense array rather than a Map:
		// the key is a small integer and this runs per queued draw
		const at = key + 1;
		let queue = queues[at];
		if (queue === undefined) {
			queue = { pool: [], count: 0 };
			queues[at] = queue;
		}
		return queue;
	}

	/**
	 * The pooled entries of the current target's queue.
	 * @type {object[]}
	 * @ignore
	 * @internal
	 */
	get _transparentPool() {
		return this.transparentQueue().pool;
	}

	/**
	 * How many entries the current target has queued.
	 * @type {number}
	 * @ignore
	 * @internal
	 */
	get _transparentCount() {
		return this.transparentQueue().count;
	}

	/**
	 * Queue one ground-shadow decal.
	 *
	 * A blob shadow is a decal, and decals are the transparent pass's first
	 * client rather than a feature of their own.
	 * @param {object} quad - the shared shadow quad
	 * @param {Matrix3d} modelMatrix - where the blob sits
	 * @param {number} tint - packed ARGB tint for the draw
	 * @param {object} [instanced] - the instanced tier's source mesh
	 * @deprecated since 20.4.0, use {@link Renderer#queueTransparent}
	 * @ignore
	 * @internal
	 */
	queueGroundShadow(quad, modelMatrix, tint, instanced) {
		this.queueTransparent(quad, modelMatrix, tint, "normal", instanced);
	}

	/**
	 * Run the transparent pass: draw everything the CURRENT render target has
	 * queued since its last drain, back-to-front, then empty that queue.
	 *
	 * Called at the three points where the world draw is genuinely finished —
	 * `Container.draw` just before a floating child, `Camera2d.draw` once the
	 * container is down, and `Application.draw` at end of frame. Deliberately
	 * NOT on a batcher transition: anything non-mesh sorting mid-scene raises
	 * one, and every mesh still to come would then paint over what was just
	 * replayed.
	 *
	 * Only the current target's queue is touched — see
	 * {@link Renderer#transparentQueue}. A post effect binds an offscreen
	 * target part-way through a scene, and a drain fired in there must not
	 * reach the world's queued geometry; each pass drains its own on the way
	 * out.
	 *
	 * Inert on a backend that never queues — the Canvas renderer has no depth
	 * buffer, and composites through the 2D context instead.
	 */
	flushTransparent() {
		if (
			this._transparentFlushing === true ||
			// A queued entry is WORLD-space geometry, and replaying it needs the
			// world projection. `Container.draw` installs the camera's screen
			// projection around a `floating` child, so a drain triggered from
			// inside that window feeds every entry screen-space clip coordinates
			// and lands it off-screen — one HUD silently deleted every ground
			// shadow in the scene. Skipping is safe rather than lossy: the
			// queue survives, and `Container.draw` drains it just BEFORE
			// opening the window, which is also where these belong — over the
			// world, under the overlay.
			(this._screenSpaceDepth ?? 0) > 0
		) {
			return;
		}
		this.flushTransparentPass();
	}

	/**
	 * Replay one target's queue, without the screen-projection guard above.
	 *
	 * Called directly when a render pass is ending: the pass owns both its
	 * target and the projection its entries were recorded under, so replaying
	 * them right there is correct whether that projection is the world's or a
	 * floating child's screen space. Left queued they would be stranded — the
	 * target is about to be unbound and its key reused by a later pass, which
	 * would then replay them somewhere they never belonged.
	 * @ignore
	 * @internal
	 */
	flushTransparentPass() {
		const queue = this.transparentQueue();
		const count = queue.count;
		if (count === 0 || this._transparentFlushing === true) {
			return;
		}
		const pool = queue.pool;
		// Back-to-front, because blending is order-dependent. Sorted over the
		// LIVE range only — pooled slots past `count` hold stale entries that
		// must not be dragged into it. Binary insertion: stable, allocation
		// free, and near-linear on the nearly-sorted input a depth-sorted
		// container already produces. If a scene ever proves that wrong, sort
		// an index scratch with typed keys rather than reaching for
		// `Array.prototype.sort` over the pool.
		for (let i = 1; i < count; i++) {
			const entry = pool[i];
			const key = entry.key;
			let lo = 0;
			let hi = i;
			while (lo < hi) {
				const mid = (lo + hi) >> 1;
				// descending: farthest first
				if (pool[mid].key < key) {
					hi = mid;
				} else {
					lo = mid + 1;
				}
			}
			for (let j = i; j > lo; j--) {
				pool[j] = pool[j - 1];
			}
			pool[lo] = entry;
		}
		// emptied BEFORE the replay, not after: a queue still holding entries
		// when the draws below run would recurse if anything on that path
		// drained again. `_transparentFlushing` guards the same door from the
		// other side, and covers the re-entry into `drawMesh`.
		queue.count = 0;
		/** @ignore @internal */
		this._transparentFlushing = true;
		try {
			// The colour the entry was queued WITH has to be put back, because
			// both backends rebuild the draw tint from `currentTint` +
			// `getGlobalAlpha()` — long since restored to the scene's values by
			// the time this replay runs.
			const tint = this.currentTint;
			const savedR = tint.r;
			const savedG = tint.g;
			const savedB = tint.b;
			// the colour's OWN alpha, which `setColor(r, g, b)` resets to 1
			const savedTintAlpha = tint.alpha;
			const savedAlpha = this.getGlobalAlpha();
			const savedShader = this.customShader;
			// `currentTransform` is one object for the frame — `save()`/
			// `restore()` push copies onto a stack rather than swapping it —
			// so each entry's view can be installed in place and the drain
			// site's own transform put back at the end.
			_savedView.copy(this.currentTransform);
			try {
				for (let i = 0; i < count; i++) {
					const entry = pool[i];
					const packed = entry.tint;
					tint.setColor(
						(packed >>> 16) & 0xff,
						(packed >>> 8) & 0xff,
						packed & 0xff,
					);
					this.setGlobalAlpha(((packed >>> 24) & 0xff) / 255);
					// read by the batchers instead of a per-mesh flag: the same
					// mesh can be queued twice under different modes, and the
					// entry is the only thing that knows which is which
					/** @ignore @internal */
					this._replayBlend = entry.blend;
					this.customShader = entry.shader ?? undefined;
					this.currentTransform.copy(entry.view);
					if (entry.instanced !== null) {
						this.drawInstancedShadow(entry.instanced, entry.matrix, entry.mesh);
					} else {
						this.drawMesh(entry.mesh, entry.matrix);
					}
					// drop the references, so a destroyed mesh is not held alive by
					// a pooled slot until that slot is next reused
					entry.mesh = null;
					entry.instanced = null;
					entry.shader = null;
				}
			} finally {
				this._replayBlend = null;
				this.customShader = savedShader;
				this.currentTransform.copy(_savedView);
				tint.setColor(savedR, savedG, savedB, savedTintAlpha);
				this.setGlobalAlpha(savedAlpha);
			}
		} finally {
			this._transparentFlushing = false;
		}
	}

	/**
	 * Run the transparent pass.
	 * @deprecated since 20.4.0, use {@link Renderer#flushTransparent}
	 */
	flushGroundShadows() {
		this.flushTransparent();
	}

	/**
	 * Drop any queued entry belonging to this mesh, so a destroyed renderable
	 * is never replayed — which would re-upload geometry for something the
	 * caller has finished with.
	 * @param {object} mesh - the renderable being torn down
	 * @ignore
	 * @internal
	 */
	removeQueuedTransparent(mesh) {
		// every target, not just the current one: a mesh destroyed during a
		// post-effect pass can still be queued in the world's queue outside it
		for (const queue of this._transparentQueues ?? []) {
			if (queue !== undefined && queue.count > 0) {
				this._removeQueuedFrom(queue, mesh);
			}
		}
	}

	/**
	 * Drop one mesh's entries from a single target's queue.
	 * @param {{pool: object[], count: number}} queue - the queue to compact
	 * @param {object} mesh - the renderable being torn down
	 * @ignore
	 * @internal
	 */
	_removeQueuedFrom(queue, mesh) {
		const count = queue.count;
		const pool = queue.pool;
		let write = 0;
		for (let read = 0; read < count; read++) {
			const entry = pool[read];
			if (entry.mesh === mesh || entry.instanced === mesh) {
				entry.mesh = null;
				entry.instanced = null;
				entry.shader = null;
				continue;
			}
			if (write !== read) {
				// order-preserving compaction: swap so no live entry is lost
				// and the emptied slot stays in the pool for reuse
				pool[read] = pool[write];
				pool[write] = entry;
			}
			write++;
		}
		queue.count = write;
	}

	/**
	 * Draw a textured triangle mesh.
	 * The mesh object must provide: `vertices` (Float32Array, x/y/z triplets),
	 * `uvs` (Float32Array, u/v pairs), `indices` (Uint16Array, triangle indices),
	 * `texture` (TextureAtlas), `vertexCount` (number), and optionally
	 * `cullBackFaces` (boolean, default true).
	 *
	 * On the GPU backends (WebGL and WebGPU — hardware depth testing),
	 * passing a `modelMatrix` selects the **retained** path: the mesh's
	 * model-space geometry stays resident on the GPU and the matrix
	 * places it, so redrawing never re-uploads vertices (see
	 * {@link Renderer#supportsRetainedMesh}). Without a matrix the
	 * vertices are taken as already CPU-projected (the 2D-camera path —
	 * the only path the Canvas renderer supports, using painter's
	 * algorithm). `Mesh.draw` selects the right form automatically.
	 * @param {Mesh} mesh - a Mesh renderable or compatible object
	 * @param {Matrix3d} [modelMatrix] - the mesh's placement, for the retained path (GPU backends)
	 */
	// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
	drawMesh(mesh, modelMatrix) {}

	/**
	 * Reset context state
	 */
	reset() {
		// Drop any ground shadows still queued (#1515) BEFORE anything below
		// can trip a drain. A reset re-inits every batcher and then switches
		// batcher, which is itself a drain point — replaying entries from the
		// frame we are abandoning would paint them after `clear()`, and on the
		// context-lost branch would draw geometry belonging to the dead
		// context. The frame is being thrown away; its shadows go with it.
		// null the references as well as the count: a discarded queue would
		// otherwise keep every queued renderable reachable until its pooled
		// slot happened to be reused
		for (const queue of this._transparentQueues ?? []) {
			if (queue === undefined) {
				continue;
			}
			for (let i = 0; i < queue.count; i++) {
				const entry = queue.pool[i];
				entry.mesh = null;
				entry.instanced = null;
				entry.shader = null;
			}
			queue.count = 0;
		}
		// belt and braces with `resetFrameState`, which is what actually
		// bounds an unbalanced bracket — `reset()` runs on a stage change and
		// on context restore, NOT once per frame
		this._screenSpaceDepth = 0;
		this.renderState.reset(this.width, this.height);
		this.resetTransform();
		this.setBlendMode(this.settings.blendMode);
		this.setColor("#000000");
		this.clearTint();
		this.cache.clear();
		this.clearMask();
	}

	/**
	 * Release any renderer-owned resources. Default is a no-op;
	 * subclasses override to tear down batchers, FBO pools, GL
	 * programs, etc. Called by {@link Application#destroy}.
	 */
	destroy() {}

	/**
	 * return a reference to the current render target corresponding canvas which this renderer draws to
	 * @returns {HTMLCanvasElement}
	 */
	getCanvas() {
		return this.renderTarget.canvas;
	}

	/**
	 * Create and return a new Canvas element, sized as requested.
	 *
	 * The instance form of {@link Renderer.createCanvas}. `app.renderer` is
	 * the renderer entry point, and a static is not reachable through an
	 * instance — so this is what user code actually calls.
	 * @param {number} width - width in pixels
	 * @param {number} height - height in pixels
	 * @param {boolean} [returnOffscreenCanvas=false] - return an OffscreenCanvas where supported
	 * @returns {HTMLCanvasElement|OffscreenCanvas} the new canvas
	 */
	createCanvas(width, height, returnOffscreenCanvas = false) {
		return createCanvas(width, height, returnOffscreenCanvas);
	}

	/**
	 * Create and return a new Canvas element (or `OffscreenCanvas` when
	 * supported and `returnOffscreenCanvas` is true). Centralized
	 * renderer-side allocator so every scratch / fallback / render-
	 * target canvas in the engine routes through the same
	 * `OffscreenCanvas`-aware path, instead of duplicating
	 * `document.createElement` calls that throw in worker contexts.
	 * @param {number} width - canvas width in pixels
	 * @param {number} height - canvas height in pixels
	 * @param {boolean} [returnOffscreenCanvas=false] - return an
	 *   `OffscreenCanvas` if the platform supports it
	 * @returns {HTMLCanvasElement|OffscreenCanvas} a new canvas of the given size
	 */
	static createCanvas(width, height, returnOffscreenCanvas = false) {
		return createCanvas(width, height, returnOffscreenCanvas);
	}

	/**
	 * Shared 1×1 fully-white canvas used as a no-op texture fallback.
	 * Renderers and renderables that need a "blank" texture binding
	 * (e.g. to satisfy a shader's sampler input when there's no real
	 * image — Kd-only `Mesh` materials, solid-color quad fills, etc.)
	 * should use this rather than allocating their own.
	 *
	 * Lazily created on first call; shared across every caller; uses
	 * `OffscreenCanvas` where supported (worker-safe). Static so it's
	 * accessible without a renderer instance (e.g. from a `Mesh`
	 * constructor that runs before the active renderer is set).
	 * @returns {HTMLCanvasElement|OffscreenCanvas} the shared 1×1 white canvas
	 */
	static getWhitePixel() {
		if (Renderer._whitePixel === null) {
			const c = Renderer.createCanvas(1, 1, true);
			const ctx = c.getContext("2d");
			if (ctx === null) {
				throw new Error(
					"Renderer.getWhitePixel: 2D context unavailable on the allocated canvas",
				);
			}
			ctx.fillStyle = "#ffffff";
			ctx.fillRect(0, 0, 1, 1);
			Renderer._whitePixel = c;
		}
		return Renderer._whitePixel;
	}

	/**
	 * return a reference to the current render target corresponding Context
	 * @returns {CanvasRenderingContext2D|WebGLRenderingContext|GPUCanvasContext}
	 */
	getContext() {
		return this.renderTarget.context;
	}

	/**
	 * return the list of supported compressed texture formats.
	 * The base implementation returns null for all formats (no GPU
	 * compressed texture support). GPU renderers (WebGL today, WebGPU
	 * once it lands) override this with the backend-specific extension /
	 * feature query.
	 * @returns {Object} an object with one key per extension family, each value is the backend's compressed-texture handle or null
	 */
	getSupportedCompressedTextureFormats() {
		return {
			astc: null,
			bptc: null,
			s3tc: null,
			s3tc_srgb: null,
			pvrtc: null,
			etc1: null,
			etc2: null,
		};
	}

	/**
	 * return true if the given compressed texture format is supported
	 * @param {number} format - a WebGL compressed texture format constant
	 * @returns {boolean}
	 */
	hasSupportedCompressedFormats(format) {
		const supportedFormats = this.getSupportedCompressedTextureFormats();
		for (const supportedFormat in supportedFormats) {
			const entry = supportedFormats[supportedFormat];
			if (entry === null || typeof entry === "undefined") {
				continue;
			}
			for (const extension in entry) {
				if (format === entry[extension]) {
					return true;
				}
			}
		}
		return false;
	}

	/**
	 * Begin capturing rendering to an offscreen buffer for post-effect processing.
	 * Call endPostEffect() after rendering to blit the result to the screen.
	 * No-op on Canvas renderer.
	 * @param {Renderable} renderable - the renderable with postEffects to apply
	 * @returns {boolean} false (Canvas renderer does not support post-effect processing)
	 * @ignore
	 * @internal
	 */
	beginPostEffect(renderable) {
		// on Canvas, only set customShader for single-effect fast path
		const effects = renderable.postEffects.filter((fx) => {
			return fx.enabled !== false;
		});
		if (effects.length === 1) {
			this.customShader = effects[0];
		} else {
			this.customShader = undefined;
		}
		return false;
	}

	/**
	 * End post-effect capture and blit the offscreen buffer to the screen
	 * through the renderable's post-effects.
	 * No-op on Canvas renderer.
	 * @param {Renderable} renderable - the renderable with postEffects to apply
	 * @ignore
	 * @internal
	 */
	// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
	endPostEffect(renderable) {}

	/**
	 * Blit a texture to the screen through a shader effect.
	 * Draws a screen-aligned quad using the given texture as source
	 * and the given shader for post-processing (e.g. scanlines, desaturation).
	 * No-op on Canvas renderer.
	 * @param {WebGLTexture} source - the source texture to blit
	 * @param {number} x - destination x position
	 * @param {number} y - destination y position
	 * @param {number} width - destination width
	 * @param {number} height - destination height
	 * @param {ShaderEffect} shader - the shader effect to apply
	 * @param {boolean} [keepBlend=false] - if true, keep current blend mode (for sprite compositing)
	 */
	// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
	blitEffect(source, x, y, width, height, shader, keepBlend) {}

	/**
	 * Sets the viewport for the renderer.
	 * Defines the affine transformation from normalized device coordinates to window coordinates.
	 * No-op on Canvas renderer.
	 * @param {number} [x=0] - x coordinate of the viewport origin
	 * @param {number} [y=0] - y coordinate of the viewport origin
	 * @param {number} [w] - width of the viewport (defaults to canvas width)
	 * @param {number} [h] - height of the viewport (defaults to canvas height)
	 */
	// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
	setViewport(x = 0, y = 0, w, h) {}

	/**
	 * Clear the current render target to transparent black (color + stencil).
	 * Used to prepare FBOs for post-effect capture.
	 * No-op on Canvas renderer.
	 */
	clearRenderTarget() {}

	/**
	 * Enable the scissor test with the given rectangle.
	 * No-op on Canvas renderer.
	 * @param {number} x - x coordinate of the scissor rectangle
	 * @param {number} y - y coordinate of the scissor rectangle
	 * @param {number} width - width of the scissor rectangle
	 * @param {number} height - height of the scissor rectangle
	 */
	// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
	enableScissor(x, y, width, height) {}

	/**
	 * Disable the scissor test, allowing rendering to the full viewport.
	 * No-op on Canvas renderer.
	 */
	disableScissor() {}

	/**
	 * Enable or disable alpha blending.
	 * No-op on Canvas renderer (Canvas always blends).
	 * @param {boolean} enable - true to enable blending, false to disable
	 */
	// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
	setBlendEnabled(enable) {}

	/**
	 * returns the current blend mode for this renderer
	 * @returns {string} blend mode
	 */
	getBlendMode() {
		return this.currentBlendMode;
	}

	/**
	 * set the current blend mode. Every renderer supports the same set —
	 * see {@link CanvasRenderer#setBlendMode} for the list and what each does.
	 * @param {string} [mode="normal"] - blend mode
	 */
	setBlendMode(mode = "normal") {
		this.currentBlendMode = mode;
	}

	/**
	 * Upload the active scene lights to the lit sprite pipeline.
	 *
	 * Called once per camera per frame by `Camera2d.draw()` (after the
	 * FBO is bound, before the world tree walk fires `Sprite.draw` for
	 * any normal-mapped sprite). GPU renderers (WebGL today, WebGPU once
	 * it lands) override this to pack the lights into the lit shader's
	 * uniform buffers; the Canvas renderer cannot do per-pixel normal-map
	 * lighting and silently ignores the call. The first time a non-empty
	 * light list is passed in Canvas mode, a one-shot console warning is
	 * emitted.
	 *
	 * Stage stays renderer-agnostic by passing the raw scene data —
	 * lights iterable and ambient color — and letting the renderer
	 * decide how to encode them.
	 * @param {Iterable<object>} [lights] - active `Light2d` instances; falsy/empty no-ops
	 * @param {object} [ambient] - ambient lighting color (0..255 RGB)
	 * @param {number} [translateX=0] - world-to-screen X translate (matches `Camera2d.draw()`)
	 * @param {number} [translateY=0] - world-to-screen Y translate
	 */
	/**
	 * Install the distance fog for the camera about to draw, or clear it with
	 * `null`.
	 *
	 * Called once per camera from `Camera2d.draw`, so fog is per view: a
	 * split-screen or minimap camera fogs independently, and a `Camera2d`
	 * clears whatever the previous camera installed. Only the mesh batchers
	 * read it — 2D content, HUDs and `floating` renderables never reach those
	 * shaders and so are never fogged.
	 *
	 * Backends without a mesh path (Canvas) inherit this and simply never read
	 * the value.
	 * @param {object|null} [fog] - resolved fog state, or null/undefined for none
	 * @ignore
	 * @internal
	 */
	setFog(fog) {
		this._fog3d = fog ?? null;
	}

	// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
	setLightUniforms(lights, ambient, translateX, translateY) {
		if (this._litPipelineWarned || !lights) {
			return;
		}
		// detect "non-empty" via `Set.size`, array length, or by iterating
		const hasAny =
			(typeof lights.size === "number" && lights.size > 0) ||
			(typeof lights.length === "number" && lights.length > 0) ||
			(typeof lights[Symbol.iterator] === "function" &&
				!lights[Symbol.iterator]().next().done);
		if (hasAny) {
			/**
			 * @ignore
			 * @internal
			 */
			this._litPipelineWarned = true;
			console.warn(
				"melonJS: Light2d normal-map lighting requires a GPU backend " +
					"(WebGL 2 or WebGPU); the Canvas fallback renders sprites without " +
					"per-pixel lighting. Light2d glow and stage lighting still draw.",
			);
		}
	}

	/**
	 * Render a `Light2d` instance.
	 *
	 * Each renderer implements its own strategy: the WebGL renderer
	 * draws lights as quads through a shared procedural radial-falloff
	 * fragment shader (no per-light texture, color and intensity
	 * encoded in the per-vertex tint so consecutive draws batch); the
	 * Canvas renderer caches a small `Gradient` config object per
	 * light in a `WeakMap` (rebuilt only when the light's radii /
	 * color / intensity change), rasterizes it with `Gradient.toCanvas()`
	 * into a single shared `CanvasRenderTarget`, and composites the
	 * result via `drawImage`. The base implementation is a no-op so
	 * renderers without a lighting path can be polymorphically
	 * substituted.
	 *
	 * Light2d itself is renderer-agnostic — it just calls
	 * `renderer.drawLight(this)` and relies on the renderer to pick
	 * the right machinery.
	 * @param {object} light - the Light2d instance to render
	 * @see Light2d
	 */
	// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
	drawLight(light) {
		// base no-op; concrete renderers override
	}

	/**
	 * Draw a TMX tile layer. Default behavior:
	 *   - if `layer.canvasRenderer` is set (preRender bake), blit the cached
	 *     offscreen canvas in a single `drawImage` call;
	 *   - otherwise delegate to the layer's TMX orientation renderer for
	 *     the per-tile loop.
	 *
	 * `WebGLRenderer` overrides this to add the procedural shader fast
	 * path on top (when `layer.renderMode === "shader"`) and fall through
	 * to this base behavior for all other layers.
	 * @param {object} layer - the TMXLayer to draw
	 * @param {Rect} rect - the visible region in world coords
	 */
	drawTileLayer(layer, rect) {
		if (layer.canvasRenderer) {
			// clamp the source rect to the cached canvas bounds — the
			// visible region can start inside the layer (camera scrolled
			// in from the origin) and extend past the layer's right /
			// bottom edge, so the source width must be reduced by
			// `rect.pos.*` to avoid reading past the canvas
			const width = Math.min(rect.width, layer.width - rect.pos.x);
			const height = Math.min(rect.height, layer.height - rect.pos.y);
			if (width <= 0 || height <= 0) {
				return;
			}
			this.drawImage(
				layer.canvasRenderer.getCanvas(),
				rect.pos.x,
				rect.pos.y,
				width,
				height,
				rect.pos.x,
				rect.pos.y,
				width,
				height,
			);
			return;
		}
		layer.getRenderer().drawTileLayer(this, layer, rect);
	}

	/**
	 * Set the current fill & stroke style color.
	 * By default, or upon reset, the value is set to #000000.
	 * @param {Color|string|Gradient} color - css color value or a Gradient object
	 */
	// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
	setColor(color) {
		// implemented by subclasses
	}

	/**
	 * get the current fill & stroke style color.
	 * @returns {Color} current global color
	 */
	getColor() {
		return this.currentColor;
	}

	/**
	 * Create a linear gradient that can be used with {@link Renderer#setColor}.
	 * @param {number} x0 - x-axis coordinate of the start point
	 * @param {number} y0 - y-axis coordinate of the start point
	 * @param {number} x1 - x-axis coordinate of the end point
	 * @param {number} y1 - y-axis coordinate of the end point
	 * @returns {Gradient} a Gradient object
	 */
	createLinearGradient(x0, y0, x1, y1) {
		return new Gradient("linear", [x0, y0, x1, y1]);
	}

	/**
	 * Create a radial gradient that can be used with {@link Renderer#setColor}.
	 * @param {number} x0 - x-axis coordinate of the start circle
	 * @param {number} y0 - y-axis coordinate of the start circle
	 * @param {number} r0 - radius of the start circle
	 * @param {number} x1 - x-axis coordinate of the end circle
	 * @param {number} y1 - y-axis coordinate of the end circle
	 * @param {number} r1 - radius of the end circle
	 * @returns {Gradient} a Gradient object
	 */
	createRadialGradient(x0, y0, r0, x1, y1, r1) {
		return new Gradient("radial", [x0, y0, r0, x1, y1, r1]);
	}

	/**
	 * Set the line dash pattern for stroke operations.
	 * @param {number[]} segments - an array of numbers specifying distances to alternately draw a line and a gap. An empty array clears the dash pattern (solid lines).
	 * @example
	 * // draw a dashed line
	 * renderer.setLineDash([10, 5]);
	 * renderer.strokeLine(0, 0, 100, 0);
	 * // clear the dash pattern
	 * renderer.setLineDash([]);
	 */
	setLineDash(segments) {
		// skip allocation if the pattern hasn't changed
		const current = this.renderState.lineDash;
		if (
			segments.length === current.length &&
			segments.every((v, i) => {
				return v === current[i];
			})
		) {
			return;
		}
		this.renderState.lineDash = segments.filter((v) => {
			return Number.isFinite(v) && v >= 0;
		});
	}

	/**
	 * Get the current line dash pattern.
	 * @returns {number[]} the current dash pattern
	 */
	getLineDash() {
		return this.renderState.lineDash;
	}

	/**
	 * return the current global alpha
	 * @returns {number}
	 */
	globalAlpha() {
		return this.currentColor.alpha;
	}

	/**
	 * check if the given rect or bounds overlaps with the renderer screen coordinates
	 * @param {Rect|Bounds} bounds
	 * @returns {boolean} true if overlaps
	 */
	overlaps(bounds) {
		return (
			bounds.left <= this.width &&
			bounds.right >= 0 &&
			bounds.top <= this.height &&
			bounds.bottom >= 0
		);
	}

	/**
	 * resizes the system canvas
	 * @param {number} width - new width of the canvas
	 * @param {number} height - new height of the canvas
	 */
	resize(width, height) {
		const canvas = this.getCanvas();
		if (width !== canvas.width || height !== canvas.height) {
			canvas.width = width;
			canvas.height = height;
			this.currentScissor[0] = 0;
			this.currentScissor[1] = 0;
			this.currentScissor[2] = width;
			this.currentScissor[3] = height;
			// publish the corresponding event
			emit(CANVAS_ONRESIZE, width, height);
		}
	}

	/**
	 * enable/disable image smoothing (scaling interpolation) for the current render target
	 * @param {boolean} [enable=false]
	 */
	setAntiAlias(enable = false) {
		if (this.settings.antiAlias !== enable) {
			this.settings.antiAlias = enable;
			this.renderTarget.setAntiAlias(enable);
		}
	}

	/**
	 * Resolve the default texture filter **mode** for this renderer, decoupled
	 * from {@link Renderer#setAntiAlias} (which controls polygon-edge MSAA on GPU
	 * backends). Honors the `textureFilter` setting (`"nearest"` / `"linear"`),
	 * falling back to the `antiAlias` setting when it's `"auto"` (the default).
	 *
	 * Backend-neutral on purpose: it returns the mode as a string so each GPU
	 * backend maps it to its own enum (WebGL `gl.LINEAR` / `gl.NEAREST`, a future
	 * WebGPU renderer `GPUFilterMode`). The Canvas renderer has no per-texture
	 * filtering, so this is informational there.
	 * @returns {"linear"|"nearest"} the resolved default filter mode
	 */
	getDefaultTextureFilter() {
		const mode = this.settings.textureFilter;
		if (mode === "linear" || mode === "nearest") {
			return mode;
		}
		// "auto" (or unset) → follow the antiAlias setting
		return this.settings.antiAlias ? "linear" : "nearest";
	}

	/**
	 * Set the default texture magnification/minification filter at runtime,
	 * decoupled from {@link Renderer#setAntiAlias}. The base implementation just
	 * records the setting (the Canvas renderer has no per-texture filtering);
	 * GPU backends override this to re-apply the filter to live textures.
	 * @param {"auto"|"nearest"|"linear"} [mode="auto"] - `"auto"` follows `antiAlias`
	 */
	setTextureFilter(mode = "auto") {
		this.settings.textureFilter = mode;
	}

	/**
	 * set/change the current projection matrix (GPU renderers only —
	 * the Canvas renderer applies projection via the 2D context's transform stack).
	 * @param {Matrix3d} matrix
	 */
	setProjection(matrix) {
		this.projectionMatrix.copy(matrix);
	}

	/**
	 * stroke the given shape
	 * @param {Rect|RoundRect|Polygon|Line|Ellipse|Bounds} shape - a shape object to stroke
	 * @param {boolean} [fill=false] - fill the shape with the current color if true
	 */
	stroke(shape, fill) {
		switch (shape.type) {
			// RoundRect
			case "RoundRect":
				if (fill) {
					this.fillRoundRect(
						shape.left,
						shape.top,
						shape.width,
						shape.height,
						shape.radius,
					);
				} else {
					this.strokeRoundRect(
						shape.left,
						shape.top,
						shape.width,
						shape.height,
						shape.radius,
					);
				}
				break;

			// Box3d — a 3D box has no 2D outline of its own, so draw the XY
			// FOOTPRINT it already hands every 2D consumer through its own
			// `getBounds()` (the body bounds, the broadphase pre-gate, and
			// this). Without a case here it fell through to the `default`
			// below, so switching on the debug panel's hitbox overlay threw
			// "Invalid geometry for fill/stroke" on any body carrying one.
			case "Box3d": {
				const footprint = shape.getBounds();
				if (fill) {
					this.fillRect(
						footprint.left,
						footprint.top,
						footprint.width,
						footprint.height,
					);
				} else {
					this.strokeRect(
						footprint.left,
						footprint.top,
						footprint.width,
						footprint.height,
					);
				}
				break;
			}

			// Rect or Bounds
			case "Rectangle":
			case "Bounds":
				if (fill) {
					this.fillRect(shape.left, shape.top, shape.width, shape.height);
				} else {
					this.strokeRect(shape.left, shape.top, shape.width, shape.height);
				}
				break;

			// Polygon or Line
			case "Polygon":
			case "Line":
				if (fill) {
					this.fillPolygon(shape);
				} else {
					this.strokePolygon(shape);
				}
				break;

			case "Ellipse":
				if (shape.angle !== 0) {
					this.save();
					this.translate(shape.pos.x, shape.pos.y);
					this.rotate(shape.angle);
					if (fill) {
						this.fillEllipse(0, 0, shape.radiusV.x, shape.radiusV.y);
					} else {
						this.strokeEllipse(0, 0, shape.radiusV.x, shape.radiusV.y);
					}
					this.restore();
				} else if (fill) {
					this.fillEllipse(
						shape.pos.x,
						shape.pos.y,
						shape.radiusV.x,
						shape.radiusV.y,
					);
				} else {
					this.strokeEllipse(
						shape.pos.x,
						shape.pos.y,
						shape.radiusV.x,
						shape.radiusV.y,
					);
				}
				break;

			// Point
			case "Point":
				this.strokePoint(shape.x, shape.y);
				break;

			default:
				throw new Error("Invalid geometry for fill/stroke");
		}
	}

	/**
	 * fill the given shape
	 * @param {Rect|RoundRect|Polygon|Line|Ellipse|Bounds} shape - a shape object to fill
	 */
	fill(shape) {
		this.stroke(shape, true);
	}

	/**
	 * tint the given image or canvas using the given color
	 * @param {HTMLImageElement|HTMLCanvasElement|OffscreenCanvas} src - the source image to be tinted
	 * @param {Color|string} color - the color that will be used to tint the image
	 * @param {string} [mode="multiply"] - the composition mode used to tint the image
	 * @returns {HTMLCanvasElement|OffscreenCanvas} a new canvas or offscreencanvas (if supported) element representing the tinted image
	 */
	tint(src, color, mode = "multiply") {
		const attributes = {
			context: "2d",
			offscreenCanvas: true,
			transparent: true,
			antiAlias: this.settings.antiAlias,
		};
		const canvasTexture = new CanvasRenderTarget(
			src.width,
			src.height,
			attributes,
		);
		const context = canvasTexture.context;

		context.fillStyle = color instanceof Color ? color.toRGB() : color;
		context.fillRect(0, 0, src.width, src.height);

		context.globalCompositeOperation = mode;
		context.drawImage(src, 0, 0);
		context.globalCompositeOperation = "destination-atop";
		context.drawImage(src, 0, 0);

		return canvasTexture.canvas;
	}

	/**
	 * Clip the region anything drawn afterwards is confined to.
	 * Implemented by subclasses.
	 * @param {number} x - x position of the clipping rectangle
	 * @param {number} y - y position of the clipping rectangle
	 * @param {number} width - width of the clipping rectangle
	 * @param {number} height - height of the clipping rectangle
	 */
	// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
	clipRect(x, y, width, height) {}

	/**
	 * Reset the current transform back to the identity matrix.
	 * Implemented by subclasses.
	 */
	resetTransform() {}

	/**
	 * Push the current transform / alpha / clip state onto an internal
	 * stack. Pair with {@link Renderer#restore}. Implemented by subclasses.
	 */
	save() {}

	/**
	 * Pop the topmost saved state from the stack, restoring transform,
	 * alpha and clip. Pair with {@link Renderer#save}. Implemented by
	 * subclasses.
	 */
	restore() {}

	/**
	 * Translate the current transform by `(x, y)`. Implemented by subclasses.
	 * @param {number} x - horizontal translation in pixels
	 * @param {number} y - vertical translation in pixels
	 */
	// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
	translate(x, y) {}

	/**
	 * Rotate the current transform by `angle` (radians). Implemented by subclasses.
	 * @param {number} angle - rotation angle in radians
	 */
	// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
	rotate(angle) {}

	/**
	 * Scale the current transform by `(x, y)`. Implemented by subclasses.
	 * @param {number} x - horizontal scale factor
	 * @param {number} y - vertical scale factor
	 */
	// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
	scale(x, y) {}

	/**
	 * Set the renderer global alpha (0..1) for subsequent draw calls.
	 * Implemented by subclasses.
	 * @param {number} alpha - opacity value in the [0..1] range
	 */
	// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
	setGlobalAlpha(alpha) {}

	/**
	 * Fill an axis-aligned rectangle with the current color.
	 * Implemented by subclasses.
	 * @param {number} x - left edge in viewport pixels
	 * @param {number} y - top edge in viewport pixels
	 * @param {number} width - rectangle width
	 * @param {number} height - rectangle height
	 */
	// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
	fillRect(x, y, width, height) {}

	/**
	 * Stroke (outline) an axis-aligned rectangle with the current color
	 * at the current {@link Renderer#lineWidth}. Implemented by subclasses.
	 * @param {number} x - left edge in viewport pixels
	 * @param {number} y - top edge in viewport pixels
	 * @param {number} width - rectangle width
	 * @param {number} height - rectangle height
	 * @param {boolean} [fill=false] - also fill the rectangle
	 */
	// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
	strokeRect(x, y, width, height, fill) {}

	/**
	 * Fill an axis-aligned ellipse with the current color.
	 * Implemented by subclasses.
	 * @param {number} x - ellipse center X in viewport pixels
	 * @param {number} y - ellipse center Y in viewport pixels
	 * @param {number} w - horizontal radius
	 * @param {number} h - vertical radius
	 */
	// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
	fillEllipse(x, y, w, h) {}

	/**
	 * Stroke (outline) an axis-aligned ellipse with the current color
	 * at the current {@link Renderer#lineWidth}. Implemented by subclasses.
	 * @param {number} x - ellipse center X in viewport pixels
	 * @param {number} y - ellipse center Y in viewport pixels
	 * @param {number} w - horizontal radius
	 * @param {number} h - vertical radius
	 * @param {boolean} [fill=false] - also fill the ellipse
	 */
	// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
	strokeEllipse(x, y, w, h, fill) {}

	/**
	 * Draw a straight line between two points with the current color
	 * at the current {@link Renderer#lineWidth}. Implemented by subclasses.
	 * @param {number} startX - start X in viewport pixels
	 * @param {number} startY - start Y in viewport pixels
	 * @param {number} endX - end X in viewport pixels
	 * @param {number} endY - end Y in viewport pixels
	 */
	// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
	strokeLine(startX, startY, endX, endY) {}

	/**
	 * Draw an image or sub-image into the framebuffer.
	 * Implemented by subclasses (`CanvasRenderer` / `WebGLRenderer` /
	 * future `WebGPURenderer`).
	 *
	 * The 9-argument signature shown here is the canonical form used
	 * throughout the engine; the concrete renderers also accept the
	 * shorter `drawImage(image, dx, dy)` and `drawImage(image, dx, dy,
	 * dWidth, dHeight)` variants from the HTML Canvas2D spec.
	 * @param {HTMLImageElement|SVGImageElement|HTMLVideoElement|HTMLCanvasElement|ImageBitmap|OffscreenCanvas|VideoFrame} image - the source image
	 * @param {number} sx - source rectangle left
	 * @param {number} sy - source rectangle top
	 * @param {number} sw - source rectangle width
	 * @param {number} sh - source rectangle height
	 * @param {number} dx - destination left
	 * @param {number} dy - destination top
	 * @param {number} dw - destination width
	 * @param {number} dh - destination height
	 */
	// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
	drawImage(image, sx, sy, sw, sh, dx, dy, dw, dh) {}

	/**
	 * A mask limits rendering elements to the shape and position of the given mask object.
	 * So, if the renderable is larger than the mask, only the intersecting part of the renderable will be visible.
	 * Mask are not preserved through renderer context save and restore.
	 * @param {Rect|RoundRect|Polygon|Line|Ellipse} [mask] - the shape defining the mask to be applied
	 * @param {boolean} [invert=false] - either the given shape should define what is visible (default) or the opposite
	 */
	// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
	setMask(mask, invert) {}

	/**
	 * disable (remove) the rendering mask set through setMask.
	 * @see Renderer#setMask
	 */
	clearMask() {}

	/**
	 * set a coloring tint for sprite based renderables
	 * @param {Color} tint - the tint color
	 * @param {number} [alpha] - an alpha value to be applied to the tint
	 */
	setTint(tint, alpha = tint.alpha) {
		// global tint color
		this.currentTint.copy(tint);
		this.currentTint.alpha *= alpha;
	}

	/**
	 * clear the rendering tint set through setTint.
	 * @see Renderer#setTint
	 */
	clearTint() {
		// reset to default
		this.currentTint.setFloat(1.0, 1.0, 1.0, 1.0);
	}

	/**
	 * Set the current per-renderable depth value. GPU batchers (WebGL
	 * today, WebGPU once it lands) push it into the vertex stream as the
	 * `z` component of each vertex — a no-op under the default
	 * orthographic projection, used by perspective (Camera3d) to scale
	 * and parallax sprites by distance.
	 *
	 * Typically called automatically by `Renderable.preDraw` from the
	 * renderable's `depth` property. User code only needs to call this
	 * directly when emitting draw calls outside of a `Renderable.draw()`
	 * — e.g. from a custom `Container.draw()` override.
	 *
	 * Honored by the save/restore stack like `setTint` / `setColor`.
	 * @param {number} depth - the depth value to set
	 * @see Renderable#depth
	 */
	setDepth(depth) {
		// A sprite's depth rides into clip-space as `aVertex.z`. Under an
		// orthographic (2D) projection that z is a painter's sort key only — the
		// world is CPU-sorted on `pos.z` — and must stay out of clip-space, or a
		// large sort value (e.g. a `baseZ + pos.y` Y-sort, or `autoDepth` on a big
		// world) clips the sprite against near/far and it silently vanishes. A
		// perspective projection (Camera3d) genuinely uses z for parallax + depth,
		// so pass it through. Perspective ⇔ the projection's w-from-z term is
		// non-zero (`val[11] === -1`); orthographic leaves it 0. Gated here (once
		// per renderable) so the per-quad batcher hot path is untouched. Mesh
		// geometry z is unaffected — only this sprite/quad sort key is zeroed.
		// val[11] is the projection's z→w (perspective-divide) term: 0 ⇒ orthographic, non-zero ⇒ perspective.
		this.currentDepth = this.projectionMatrix.val[11] !== 0 ? depth : 0;
	}

	/**
	 * creates a Blob object representing the last rendered frame
	 * @param {string} [type="image/png"] - A string indicating the image format
	 * @param {number} [quality] - A Number between 0 and 1 indicating the image quality to be used when creating images using file formats that support lossy compression (such as image/jpeg or image/webp). A user agent will use its default quality value if this option is not specified, or if the number is outside the allowed range.
	 * @returns {Promise} A Promise returning a Blob object representing the last rendered frame
	 * @example
	 * renderer.convertToBlob().then((blob) => console.log(blob));
	 */
	toBlob(type = "image/png", quality) {
		return this.renderTarget.toBlob(type, quality);
	}

	/**
	 * creates an ImageBitmap object of the last frame rendered
	 * (not supported by standard Canvas)
	 * @param {string} [type="image/png"] - A string indicating the image format
	 * @param {number} [quality] - A Number between 0 and 1 indicating the image quality to be used when creating images using file formats that support lossy compression (such as image/jpeg or image/webp). A user agent will use its default quality value if this option is not specified, or if the number is outside the allowed range.
	 * @returns {Promise} A Promise returning an ImageBitmap.
	 * @example
	 * renderer.transferToImageBitmap().then((image) => console.log(image));
	 */
	toImageBitmap(type = "image/png", quality) {
		return this.renderTarget.toImageBitmap(type, quality);
	}

	/**
	 * returns a data URL containing a representation of the last frame rendered
	 * @param {string} [type="image/png"] - A string indicating the image format
	 * @param {number} [quality] - A Number between 0 and 1 indicating the image quality to be used when creating images using file formats that support lossy compression (such as image/jpeg or image/webp). A user agent will use its default quality value if this option is not specified, or if the number is outside the allowed range.
	 * @returns {Promise} A Promise returning a string containing the requested data URL.
	 * @example
	 * renderer.toDataURL().then((dataURL) => console.log(dataURL));
	 */
	toDataURL(type = "image/png", quality) {
		return this.renderTarget.toDataURL(type, quality);
	}

	/**
	 * Capture the current frame — everything drawn to the active framebuffer /
	 * canvas so far — into a {@link Texture2d}, on the GPU where possible (no
	 * `readPixels` round-trip). The fourth member of the {@link Renderer#toDataURL}
	 * / {@link Renderer#toBlob} / {@link Renderer#toImageBitmap} family — "the
	 * current frame as X" — and the only one whose result can stay GPU-resident,
	 * ready to feed a shader as a screen texture (water refraction, heat haze,
	 * frosted glass).
	 *
	 * Abstract on the base renderer: the concrete backends implement it —
	 * {@link WebGLRenderer#toFrameTexture} (a `copyTexImage2D` into a live GPU
	 * texture, bound as an extra sampler via {@link ShaderEffect#setTexture}) and
	 * {@link CanvasRenderer#toFrameTexture} (an offscreen-canvas copy, for family
	 * parity — custom shaders don't run under Canvas).
	 * @param {object} [options]
	 * @param {Texture2d|null} [options.target] - controls the destination: omit
	 *   for the shared renderer slot (default); pass a capture previously
	 *   returned by this method to refresh it in place; pass `null` to mint a
	 *   fresh, caller-owned capture
	 * @param {Bounds|{x: number, y: number, width: number, height: number}} [options.region] - capture
	 *   only this sub-region of the frame — a {@link Bounds} (or any
	 *   `{x, y, width, height}`); from a {@link Rect} pass `rect.getBounds()`.
	 *   NOTE the origin differs by backend: WebGL uses framebuffer coords
	 *   (**bottom-left** origin), Canvas uses **top-left** — relevant under
	 *   `video.AUTO`.
	 * @returns {Texture2d} a texture holding the captured frame
	 * @example
	 * effect.setTexture("uScene", renderer.toFrameTexture());
	 */
	// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
	toFrameTexture(options) {
		throw new Error("toFrameTexture() is not implemented by this renderer");
	}
}

// Backing field for `Renderer.getWhitePixel()` — declared outside the
// class body so it's initialized to null at module load (static class
// fields aren't universally supported in our transpile target).
/**
 * @ignore
 * @internal
 */
Renderer._whitePixel = null;
